import React from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import DateChip from "components/DateChip";
import StyledModal from "components/Modal";
import WarningBox from "components/WarningBox";

import { useAppDispatch } from "store/hooks";

import {
  deleteIbanInvoiceByUid,
  IbanInvoice,
} from "store/features/ibanInvoiceSlice";

import { getPriceFormat } from "shared/functions";
import { formatIban } from "shared/functions/formatIban";

import dayjs from "dayjs";

interface InvoiceDeleteModalProps {
  isOpen: boolean;
  invoice: IbanInvoice | null;
  onAfterSubmit?: () => Promise<void>;
  onClose: () => void;
}

const InvoiceDeleteModal: React.FC<InvoiceDeleteModalProps> = (props) => {
  const { isOpen, invoice, onAfterSubmit, onClose } = props;
  const dispatch = useAppDispatch();

  const expirationText = invoice?.expirationDateTimeUtc
    ? `діє до ${dayjs
        .utc(invoice?.expirationDateTimeUtc)
        .local()
        .format("DD MMM YYYY, HH:mm")}`
    : "Без терміну дії";

  const onSubmit = async () => {
    invoice?.id &&
      (await dispatch(deleteIbanInvoiceByUid(invoice?.id)).unwrap());
    await onAfterSubmit?.();
    onCloseModal();
  };

  const onCloseModal = () => {
    onClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onCloseModal}
      title="Видалити посилання"
      content={
        <>
          <WarningBox content="Клієнт міг уже здійснити оплату або розпочати процес оплати за цим посиланням. Його видалення вплине лише на майбутні платежі й не змінить статус уже оплачених або тих, що наразі перебувають у процесі оплати." />
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid #E5E5E5",
              p: "12px 16px",
              m: "16px 0",
            }}
          >
            <DateChip date={expirationText} />
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, overflowWrap: "anywhere" }}
              >
                {invoice?.organizationName}
              </Typography>
              <Typography variant="subtitle2" color="grey700" sx={{ mb: 0.5 }}>
                {formatIban(invoice?.iban || "")}
              </Typography>
              <Typography
                variant="subtitle2"
                color="grey700"
                sx={{ mb: 0.5, overflowWrap: "anywhere" }}
              >
                {invoice?.paymentPurpose || ""}
              </Typography>
              <Typography variant="subtitle2" color="grey700" sx={{ mb: 0.5 }}>
                {getPriceFormat(invoice?.amount || 0) || ""}₴
              </Typography>
            </Box>
            {invoice?.notes && (
              <>
                <Box sx={{ m: "12px 0", border: "1px solid #E5E5E5" }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="grey700"
                    sx={{ mb: 0.5, overflowWrap: "anywhere" }}
                  >
                    {invoice?.notes || ""}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </>
      }
      onAction={onSubmit}
      actionBtnText="Видалити остаточно"
      actionBtnColor="error"
      actionBtnBorder
    />
  );
};

export default InvoiceDeleteModal;
